import apiClient from './apiClient';
import ssoService from './ssoService';
import { detectBrowser } from '../utils/browserDetection';
import { metaPixelService } from '../lib/metaPixel';
import { trackAuthAttempt, trackAuthSuccess, trackAuthFailure, trackPopupBlocked } from '../firebaseConfig';

interface GoogleAuthResponse {
  url: string;
  state: string;
}

class GoogleAuthService {
  /**
   * Initiates Google authentication through JobProt
   * This makes it appear as if MockTrail has native Google sign-in
   */
  async initiateGoogleAuth(forceRedirect: boolean = false): Promise<any> {
    try {
      console.log('Initiating Google authentication through JobProt');
      
      // Check if we should use redirect flow
      const { isMobile } = detectBrowser();
      const useRedirectFlow = forceRedirect || isMobile;
      
      console.log('Device detection:', { isMobile, useRedirectFlow });
      // Track authentication attempt
      metaPixelService.trackAuthAttempt('google', 'login_page', {
        source: 'google_auth_service',
        timestamp: new Date().toISOString()
      });
      
      // Track in Firebase Analytics
      trackAuthAttempt('google', 'login_page', {
        source: 'google_auth_service',
        timestamp: new Date().toISOString()
      });
      
      // Get Google auth URL from MockTrail backend (which proxies to JobProt)
      const flowType = useRedirectFlow ? 'redirect' : 'popup';
      const response = await apiClient.get<GoogleAuthResponse>(`/auth/google?flowType=${flowType}`, undefined, false);
      
      const { url, state } = response;
      
      if (!url || !state) {
        throw new Error('Invalid Google auth response');
      }
      
      // Store state in sessionStorage for redirect flow
      if (useRedirectFlow) {
        sessionStorage.setItem('google_auth_state', state);
        sessionStorage.setItem('google_auth_redirect', 'true');
        sessionStorage.setItem('google_auth_provider', 'google');
      }
      
      console.log('Opening Google auth', useRedirectFlow ? 'redirect' : 'popup');
      
      if (useRedirectFlow) {
        // For mobile browsers, use redirect flow
        // Store state for callback handling
        sessionStorage.setItem('google_auth_state', state);
        sessionStorage.setItem('google_auth_redirect', 'true');
        sessionStorage.setItem('google_auth_provider', 'google');
        
        // Redirect to Google auth - use the URL as is from backend
        // The backend should already have the correct redirect_uri configured
        window.location.href = url;
        
        // Return a pending promise that won't resolve (page will redirect)
        return new Promise(() => {
          metaPixelService.trackAuthSuccess('google', 'login_page', {
            source: 'google_auth_service',
            timestamp: new Date().toISOString()
          });
          
          // Track in Firebase Analytics
          trackAuthSuccess('google', 'login_page', {
            source: 'google_auth_service',
            timestamp: new Date().toISOString()
          });
        });
      } else {
        // For desktop browsers, use popup flow
        const popupResponse = await this.openGoogleAuthPopup(url);
        
        console.log('Google auth popup completed:', popupResponse);
        
        // Exchange the code for tokens
        if (popupResponse.params.code) {
          const tokenResponse = await ssoService.exchangeToken({
            code: popupResponse.params.code,
            state: state
          });
          
          console.log('Google auth token exchange successful');
          
          // Return the response for the component to handle
          return tokenResponse;
        } else {
          throw new Error('No authorization code received from Google auth');
        }
      }
    } catch (error) {
      console.error('Google authentication failed:', error);
      
              // Track authentication failure
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        metaPixelService.trackAuthFailure('google', 'login_page', 'authentication_failed', errorMessage, {
          source: 'google_auth_service',
          timestamp: new Date().toISOString()
        });
        
        // Track in Firebase Analytics
        trackAuthFailure('google', 'login_page', 'authentication_failed', errorMessage, {
          source: 'google_auth_service',
          timestamp: new Date().toISOString()
        });
      
      throw error;
    }
  }
  
  /**
   * Opens Google auth popup and waits for completion
   */
  private openGoogleAuthPopup(url: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const width = 500;
      const height = 600;
      const left = window.innerWidth / 2 - width / 2;
      const top = window.innerHeight / 2 - height / 2;
      
      const popup = window.open(
        url,
        'Google Sign-In',
        `width=${width},height=${height},left=${left},top=${top},location=yes,toolbar=no,menubar=no`
      );
      
      if (!popup) {
        // Track popup blocked event
        metaPixelService.trackPopupBlocked('google', 'login_page', {
          source: 'google_auth_service',
          timestamp: new Date().toISOString(),
          error_type: 'popup_blocked',
          error_message: 'Popup was blocked. Please allow popups for this site.'
        });
        
        // Track in Firebase Analytics
        trackPopupBlocked('google', 'login_page', {
          source: 'google_auth_service',
          timestamp: new Date().toISOString(),
          error_type: 'popup_blocked',
          error_message: 'Popup was blocked. Please allow popups for this site.'
        });
        
        reject(new Error('Popup was blocked. Please allow popups for this site.'));
        return;
      }
      
      let authCompleted = false;
      
      // Listen for messages from the popup
      const messageListener = (event: MessageEvent) => {
        // Accept messages from JobProt or the expected domain
        if (!event.origin.includes('jobprot') && !event.origin.includes('localhost')) {
          return;
        }
        
        if (event.data && event.data.type === 'sso_complete') {
          console.log('Received Google auth completion message');
          authCompleted = true;
          window.removeEventListener('message', messageListener);
          clearInterval(checkClosed);
          
          // The popup should close itself, but we can try to close it
          if (!popup.closed) {
            popup.close();
          }
          
          resolve(event.data);
        }
      };
      
      window.addEventListener('message', messageListener);
      
      // Check if popup was closed without completing auth
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageListener);
          
          if (!authCompleted) {
            // Track authentication cancelled
            metaPixelService.trackAuthFailure('google', 'login_page', 'authentication_cancelled', 'Google authentication was cancelled', {
              source: 'google_auth_service',
              timestamp: new Date().toISOString()
            });
            
            // Track in Firebase Analytics
            trackAuthFailure('google', 'login_page', 'authentication_cancelled', 'Google authentication was cancelled', {
              source: 'google_auth_service',
              timestamp: new Date().toISOString()
            });
            
            reject(new Error('Google authentication was cancelled'));
          }
        }
      }, 1000);
    });
  }
  
  /**
   * Handle OAuth callback for redirect flow (used in mobile browsers)
   */
  async handleOAuthCallback(): Promise<any> {
    try {
      console.log('Handling Google OAuth callback');
      
      // Get code and state from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const stateFromUrl = urlParams.get('state');
      
      // Get stored state from sessionStorage
      const storedState = sessionStorage.getItem('google_auth_state');
      const isRedirectFlow = sessionStorage.getItem('google_auth_redirect') === 'true';
      
      if (!isRedirectFlow) {
        console.log('Not a redirect flow callback, ignoring');
        return null;
      }
      
      if (!code) {
        throw new Error('No authorization code in callback URL');
      }
      
      // Verify state matches (if provided in URL)
      if (stateFromUrl && storedState && stateFromUrl !== storedState) {
        throw new Error('State mismatch in OAuth callback');
      }
      
      // Use stored state if not in URL (some OAuth providers don't return state in callback)
      const state = stateFromUrl || storedState;
      
      if (!state) {
        throw new Error('No state available for OAuth callback');
      }
      
      console.log('Exchanging code for tokens in redirect flow');
      
      // Exchange the code for tokens
      const tokenResponse = await ssoService.exchangeToken({
        code: code,
        state: state
      });
      
      console.log('Google auth token exchange successful (redirect flow)');
      
      // Clean up sessionStorage
      sessionStorage.removeItem('google_auth_state');
      sessionStorage.removeItem('google_auth_redirect');
      sessionStorage.removeItem('google_auth_provider');
      
      // Clean up URL
      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      url.searchParams.delete('state');
      window.history.replaceState({}, document.title, url.pathname);
      
      return tokenResponse;
    } catch (error) {
      console.error('OAuth callback handling failed:', error);
      
      // Clean up on error
      sessionStorage.removeItem('google_auth_state');
      sessionStorage.removeItem('google_auth_redirect');
      sessionStorage.removeItem('google_auth_provider');
      
      throw error;
    }
  }
}

export const googleAuthService = new GoogleAuthService();
export default googleAuthService;